import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import { apiDelete, apiGet, apiPatch, apiPost } from 'services';
import { setShowSuccess } from 'actions/ui';
import { adminLoading } from 'actions/admin';
import { Picker, SystemText } from 'e4shared';
import MetricPicker from 'components/admin/forms/metricPicker';
import { setDashboardCollection } from 'actions/dashboard';
import { getValidFor } from 'helpers/goals';
import SetHistoricGoals from 'components/general/goalSummary/components/setHistoricGoals';

export const AdminGoals = () => {
    const dispatch = useDispatch();

    const nonWeekly = ['LKIN', 'GONG'];
    const [periods] = React.useState([
        { id: 'W', name: SystemText('General/Periods/Weekly') },
        { id: 'M', name: SystemText('General/Periods/Monthly') },
        { id: 'Q', name: SystemText('General/Periods/Quarterly') },
        { id: 'Y', name: SystemText('General/Periods/Yearly') },
    ]);

    const [availablePeriods, setAvailablePeriods] = React.useState(periods);
    const [data, setData] = React.useState([]);
    const [roles, setRoles] = React.useState([]);
    const [showRolePicker, setShowRolePicker] = React.useState(false);
    const [showGoals, setShowGoals] = React.useState(false);
    const [processedData, setProcessedData] = React.useState(false);
    const [pickerItem, setPickerItem] = React.useState(null);
    const user = useSelector(state => state.user.user);
    const loading = useSelector(state => state.admin.loading);

    const metrics = useSelector(state => state.dashboard.metrics);

    const translations = {
        fields: {
            displayName: {
                label: SystemText('Admin/Quota/Fields/DisplayName/Label'),
                help: SystemText('Admin/Quota/Fields/DisplayName/Help'),
            },
            roles: {
                label: SystemText('Admin/Quota/Fields/Roles/Label'),
                help: SystemText('Admin/Quota/Fields/Roles/Help'),
            },
            metric: {
                label: SystemText('Admin/Quota/Fields/Metric/Label'),
                help: SystemText('Admin/Quota/Fields/Metric/Help'),
                error: SystemText('Admin/Quota/Fields/Metric/Error'),
            },
            period: {
                label: SystemText('Admin/Quota/Fields/Period/Label'),
                help: SystemText('Admin/Quota/Fields/Period/Help'),
                error: SystemText('Admin/Quota/Fields/Period/Error'),
            },
            admin: {
                label: SystemText('Admin/Quota/Fields/Admin/Label'),
                help: SystemText('Admin/Quota/Fields/Admin/Help'),
                user: SystemText('Admin/Quota/Fields/Admin/Options/User'),
                admin: SystemText('Admin/Quota/Fields/Admin/Options/Admin'),
            },
            target: {
                label: SystemText('Admin/Quota/Fields/Target/Label'),
                help: SystemText('Admin/Quota/Fields/Target/Help'),
                placeholder: SystemText('Admin/Quota/Fields/Target/Placeholder'),
            },
        },
        table: {
            goal: SystemText('Admin/Quota/Table/Goal'),
            integration: SystemText('Admin/Quota/Table/Integration'),
            period: SystemText('Admin/Quota/Table/Period'),
            roles: SystemText('Admin/Quota/Table/Roles'),
            admin: SystemText('Admin/Quota/Table/Admin'),
            setGoals: SystemText('Admin/Quota/Table/SetGoals'),
        },
        picker: {
            label: x => SystemText('Admin/Quota/Picker/Label', x),
            entity: SystemText('Admin/Quota/Picker/Entity'),
        },
        cannotDelete: SystemText('Admin/Quota/CannotDelete'),
        success: SystemText('Admin/Quota/Success'),
        deleteSuccess: SystemText('Admin/Quota/DeleteSuccess'),
    };

    const setLoading = value => dispatch(adminLoading(value));

    React.useEffect(() => {
        user?.guid && load();
    }, []);

    const options = {
        ...DefaultOptions(),
        cannotDeleteMessage: translations.cannotDelete,
        prepareForSave: goal => {
            goal.valid_For = getValidFor(goal.period, user.fy_Start_Date);
        },
        label: translations.table.goal,
        extraOptions: [
            {
                btnClass: 'btn-primary',
                label: translations.table.setGoals,
                icon: 'fa-calendar',
                disabled: d => !d.admin,
                onClick: d => setShowGoals(d),
            },
        ],
    };

    const fields = [
        {
            label: translations.fields.displayName.label,
            help: translations.fields.displayName.help,
            type: 'text',
            mandatory: true,
            dataProperty: 'name',
            maxLength: 100,
            generalErrors: true,
        },
        {
            label: translations.fields.metric.label,
            help: translations.fields.metric.help,
            type: 'special',
            onChange: value => {
                if (nonWeekly.some(x => x === value?.type)) {
                    setAvailablePeriods(periods.filter(p => p.id !== 'W'));
                } else {
                    setAvailablePeriods(periods);
                }
            },
            renderer: (values, fieldProps) => (
                <MetricPicker
                    label={translations.fields.metric.label}
                    help={translations.fields.metric.help}
                    mandatory
                    hideSubMetrics
                    hideRealtimeMetrics
                    limitTypes={['RT']}
                    {...fieldProps}
                />
            ),
            dataProperty: 'metric_Json',
            mandatory: true,
            validation: values => (values.metric_Json?.metricId ? '' : translations.fields.metric.error),
        },
        {
            label: translations.fields.period.label,
            help: translations.fields.period.help,
            dataProperty: 'period',
            type: 'select',
            data: availablePeriods,
            mandatory: true,
            validation: values =>
                !availablePeriods.some(x => x.id === values.period) ? translations.fields.period.error : '',
        },
        {
            label: translations.fields.roles.label,
            help: translations.fields.roles.help,
            type: 'select',
            data: roles,
            allowMultiple: true,
            dataProperty: 'role_Ids',
        },
        {
            label: translations.fields.admin.label,
            help: translations.fields.admin.help,
            type: 'toggle',
            yesLabel: translations.fields.admin.admin,
            noLabel: translations.fields.admin.user,
            dataProperty: 'admin',
        },
        {
            label: translations.fields.target.label,
            help: translations.fields.target.help,
            placeholder: translations.fields.target.placeholder,
            type: 'number',
            dataProperty: 'this_Target',
            toggleProperty: 'admin',
            mandatory: true,
        },
    ];

    const tableColumns = [
        { label: translations.table.goal, dataProperty: 'name' },
        { label: translations.table.integration, dataProperty: 'integration', className: 'hidden-xs' },
        { label: translations.table.period, dataProperty: 'period_Name', className: 'hidden-xs' },
        {
            label: translations.table.roles,
            dataProperty: 'role_Ids',
            showCount: true,
            onClick: item => {
                setPickerItem(item);
                setShowRolePicker(true);
            },
        },
        {
            label: translations.table.admin,
            dataProperty: 'admin',
            className: 'hidden-xs',
            format: (v, d) => (d.admin ? translations.fields.admin.admin : translations.fields.admin.user),
        },
    ];

    const load = () => {
        setLoading(true);
        setProcessedData(false);
        apiGet('all-fields', user.guid, resp => {
            const allFields = Array.isArray(resp) ? resp : [resp];
            dispatch(setDashboardCollection('metrics', allFields));
        });
        apiGet('role', user.guid, response => setRoles(response));
        apiGet('all-quotas', user.guid, response => {
            const sorted = response
                .map(r => {
                    r.metric_Json =
                        r.metric_Json ||
                        JSON.stringify({
                            metricId: r.metric_Id,
                            metricRef: r.metric_Id.toString(),
                            subMetricId: null,
                            subMetricValues: [],
                            type: 'CRM',
                        });

                    const parsed = JSON.parse(r.metric_Json);

                    return {
                        ...r,
                        metric_Json: parsed,
                        integration: r.integration || parsed.type,
                        period_Name: periods.find(p => p.id === r.period)?.name,
                    };
                })
                .sort((a, b) => (a.name > b.name ? 1 : -1));
            setData(sorted);
            setLoading(false);
        });
    };

    React.useEffect(() => {
        if (data?.length > 0 && metrics?.length > 0 && !processedData) {
            data.map(x => {
                const metric = x.crm
                    ? metrics.find(m => m.ref === 'CRM' && x.metric_Id === m.id)
                    : metrics.find(m => m.ref !== 'CRM' && x.metric_Id === m.id);

                if (metric?.ratio_Id1) {
                    x.this_Target = x.this_Target * 100;
                }
            });

            setProcessedData(true);
        }
    }, [data, metrics]);

    const add = (quota, callback) => {
        quota.metric_Id = quota.metric_Json.metricId;
        quota.CRM = quota.metric_Json.type === 'CRM';
        quota.metric_Json = JSON.stringify(quota.metric_Json);

        const metric = metrics
            .filter(x => (quota.CRM ? x.ref === 'CRM' : x.ref !== 'CRM'))
            .find(x => x.id === quota.metric_Id);

        if (metric?.ratio_Id1) quota.this_Target = quota.this_Target ? quota.this_Target / 100 : quota.this_Target;

        apiPost('quota', quota, user.guid, () => {
            dispatch(setShowSuccess(translations.success));
            load();
            callback?.();
        });
    };

    const update = (quota, callback) => {
        quota.metric_Id = quota.metric_Json.metricId;
        quota.CRM = quota.metric_Json.type === 'CRM';

        const metric = metrics
            .filter(x => (quota.CRM ? x.ref === 'CRM' : x.ref !== 'CRM'))
            .find(x => x.id === quota.metric_Id);

        if (metric?.ratio_Id1) quota.this_Target = quota.this_Target ? quota.this_Target / 100 : quota.this_Target;

        quota.metric_Json = JSON.stringify(quota.metric_Json);
        apiPatch('quota', quota, user.guid, () => {
            dispatch(setShowSuccess(translations.success));
            load();
            callback?.();
        });
    };

    const remove = (ids, callback) => {
        if (!Array.isArray(ids)) ids = [ids];
        ids.forEach((id, idx) => {
            apiDelete(`quota/${id}`, id, user.guid, () => {
                if (idx + 1 === ids.length) {
                    dispatch(setShowSuccess(translations.deleteSuccess));
                    load();
                    callback?.();
                }
            });
        });
    };

    return (
        <div>
            <SimpleAdmin
                data={data}
                loading={loading}
                fields={fields}
                options={options}
                columns={tableColumns}
                remove={remove}
                update={update}
                add={add}
            />
            <Picker
                title={translations.picker.label(pickerItem?.name)}
                entity={translations.picker.entity}
                data={roles}
                value={pickerItem?.role_Ids}
                show={showRolePicker}
                onSave={ids => {
                    update({ ...pickerItem, role_Ids: ids }, () => {
                        setPickerItem(null);
                        setShowRolePicker(false);
                    });
                }}
                onClose={() => {
                    setPickerItem(null);
                    setShowRolePicker(false);
                    dispatch(setShowSuccess(false));
                }}
                itemPrefix="street-view"
            />
            <SetHistoricGoals goal={showGoals} show={showGoals} onClose={() => setShowGoals(null)} profile={{ id: 0 }} />
        </div>
    );
};

export default AdminGoals;
